// views/Tarefas/useSolicitacaoActions.ts
//
// Busca uma Solicitação pelo id e dá acesso a todas as ações do modal de detalhe (mudar
// status/tipo/departamento/dependência/solicitante/projeto, editar título/descrição,
// autoatribuir, anexos, subtarefas, comentários, excluir e direcionar) — sem depender de
// nenhuma lista já carregada em outra tela. Usado hoje só por SolicitacaoModalGlobal.tsx.
//
// Cronômetro (Iniciar/Pausar/Finalizar) fica de fora de propósito: ele é o único pedaço que as
// ações rápidas de linha do Kanban/Tabela/Lista da Central de Solicitações também usam fora do
// modal (handleStartTimer/handlePauseTimer/handleFinishTimer em TarefasView.tsx) — continua
// vivendo só lá. SolicitacaoModalGlobal.tsx tem sua própria cópia pequena e independente.
//
// Depois de qualquer mudança, dispara um dos três eventos abaixo — é assim que uma lista que
// esteja montada ao mesmo tempo (o quadro da Central de Solicitações) se mantém atualizada sem
// esta hook precisar conhecer nem uma lista.
import { useEffect, useState } from 'react';
import { supabase } from '../../src/lib/supabase';
import { api } from '../../src/services/api';
import { anexarArquivoNaTarefa, removerAnexoDaTarefa, enviarAnexo } from '../../src/lib/anexosTarefa';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import { logAction, logTarefaStatusEvent, logUsageEvent } from '../../src/lib/logger';
import { abrirSolicitacao } from '../../src/lib/abrirSolicitacao';
import { ocultarSeApagado, mensagemErroApagarComentario } from '../../src/lib/comentarioApagado';
import { equipeDoDepartamento, etapasDoDepartamento } from './solicitacaoTipos';
import type {
  Tarefa, ComentarioTarefa, SolicitacaoStatus, SolicitacaoTipo, SolicitacaoDepartamento,
  TarefaPrioridade, TarefaStatus,
} from '../../types';

export const SOLICITACAO_ATUALIZADA_EVENTO = 'vigorhub:solicitacao-atualizada';
export const SOLICITACAO_CRIADA_EVENTO = 'vigorhub:solicitacao-criada';
export const SOLICITACAO_EXCLUIDA_EVENTO = 'vigorhub:solicitacao-excluida';

// Espelha solicitacao_status no status genérico — mesma regra de TarefasView.tsx original,
// mantém "ocultar concluídas", calendário e KPIs funcionando sem mudança nenhuma.
const mapSolicitacaoStatusToStatus = (s: SolicitacaoStatus): TarefaStatus => {
  if (s === 'novo') return 'a_iniciar';
  if (s === 'concluido') return 'concluido';
  if (s === 'rejeitado') return 'cancelado';
  return 'em_andamento'; // em_analise | em_desenvolvimento | em_teste
};

const isTipoOutro = (tipo?: SolicitacaoTipo | null): boolean =>
  tipo === 'outro' || tipo === 'outro_tecnologia';

export const useSolicitacaoActions = (taskId: string | null) => {
  const { user, profile } = useAuth();
  const { addToast } = useToast();
  const usoFuncionalidade = 'solicitacoes';

  const [task, setTask] = useState<Tarefa | null>(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [subtarefas, setSubtarefas] = useState<Tarefa[]>([]);
  const [comments, setComments] = useState<ComentarioTarefa[]>([]);
  const [requestingDelete, setRequestingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [direcionando, setDirecionando] = useState(false);

  useEffect(() => {
    setTask(null);
    setSubtarefas([]);
    setComments([]);
    setNotFound(false);
    setRequestingDelete(false);
    setDirecionando(false);

    if (!taskId) return;
    let cancelado = false;
    setLoading(true);

    (async () => {
      const { data, error } = await supabase.from('tarefas').select('*').eq('id', taskId).single();
      if (cancelado) return;
      if (error || !data) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setTask(data as Tarefa);
      setLoading(false);

      const [{ data: filhas }, { data: coments }] = await Promise.all([
        supabase.from('tarefas').select('*').eq('parent_id', taskId),
        supabase.from('comentarios_tarefas').select('*, user:profiles(*)').eq('tarefa_id', taskId).order('created_at', { ascending: true }),
      ]);
      if (cancelado) return;
      setSubtarefas((filhas || []) as Tarefa[]);
      setComments(((coments || []) as any[]).map(ocultarSeApagado) as any);
    })();

    return () => { cancelado = true; };
  }, [taskId]);

  // Tempo real da solicitação aberta (#6352): o carregamento acima é feito uma vez só, então
  // a janela ficava parada enquanto outra pessoa mudava status, prazo, subtarefas ou comentava.
  // Comentário também é conferido a cada 30 s — se a tabela não estiver no tempo real do banco,
  // ainda chega.
  useEffect(() => {
    if (!taskId) return;
    let vivo = true;
    const recarregarComentarios = async () => {
      const { data } = await supabase.from('comentarios_tarefas').select('*, user:profiles(*)').eq('tarefa_id', taskId).order('created_at', { ascending: true });
      if (vivo && data) setComments((data as any[]).map(ocultarSeApagado) as any);
    };
    const recarregarFilhas = async () => {
      const { data } = await supabase.from('tarefas').select('*').eq('parent_id', taskId);
      if (vivo && data) setSubtarefas(data as Tarefa[]);
    };
    const canal = supabase
      .channel(`solicitacao-${taskId}-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'tarefas', filter: `id=eq.${taskId}` }, (payload: any) => {
        if (vivo && payload.new) setTask(prev => (prev ? { ...prev, ...payload.new } : prev));
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tarefas', filter: `parent_id=eq.${taskId}` }, () => { recarregarFilhas(); })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'comentarios_tarefas', filter: `tarefa_id=eq.${taskId}` }, () => { recarregarComentarios(); })
      .subscribe();
    const t = setInterval(recarregarComentarios, 30_000);
    return () => { vivo = false; clearInterval(t); supabase.removeChannel(canal); };
  }, [taskId]);

  // ── Trocar campo com atualização otimista, aviso e desfazer se o Supabase recusar ──
  const patchAndSave = (
    patch: Partial<Tarefa>,
    opts: { acaoLog?: string; auditoria?: () => Promise<void> } = {},
  ) => {
    if (!task) return;
    const previous = task;
    setTask({ ...previous, ...patch });
    window.dispatchEvent(new CustomEvent(SOLICITACAO_ATUALIZADA_EVENTO, { detail: { id: previous.id, patch } }));

    (async () => {
      const { error } = await supabase.from('tarefas').update(patch).eq('id', previous.id);
      if (error) {
        setTask(previous);
        window.dispatchEvent(new CustomEvent(SOLICITACAO_ATUALIZADA_EVENTO, { detail: { id: previous.id, patch: previous } }));
        // Mensagem do banco (ex.: violação de CHECK) não ajuda quem usa; o detalhe fica no console.
        console.error('Erro ao atualizar a solicitação:', error);
        addToast('Não foi possível salvar essa alteração. A tela voltou ao valor anterior; tente de novo.', 'error');
        return;
      }
      if (opts.acaoLog) logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: opts.acaoLog, origem: 'acao' });
      if (opts.auditoria) await opts.auditoria();
    })();
  };

  const onStatusChange = (newStatus: SolicitacaoStatus) => {
    if (!task) return;
    const oldStatus = task.solicitacao_status ?? undefined;
    patchAndSave(
      {
        solicitacao_status: newStatus,
        status: mapSolicitacaoStatusToStatus(newStatus),
        data_conclusao: newStatus === 'concluido' ? new Date().toISOString() : null,
      },
      {
        acaoLog: 'mudar_status_tarefa',
        auditoria: async () => {
          await logAction(user?.id, 'UPDATE_TAREFA_STATUS', { tarefa_id: task.id, old_status: oldStatus, new_status: newStatus });
          await logTarefaStatusEvent({ tarefaId: task.id, userId: user?.id, fromStatus: oldStatus, toStatus: newStatus });
        },
      },
    );
  };

  const onTipoChange = (newTipo: SolicitacaoTipo | null) => {
    if (!task) return;
    const oldTipo = task.tipo_solicitacao ?? null;
    patchAndSave(
      { tipo_solicitacao: newTipo, tipo_outro_detalhe: isTipoOutro(newTipo) ? (task.tipo_outro_detalhe ?? null) : null },
      {
        acaoLog: 'mudar_tipo_solicitacao',
        auditoria: async () => {
          await logAction(user?.id, 'UPDATE_TAREFA_TIPO_SOLICITACAO', { tarefa_id: task.id, old_tipo: oldTipo, new_tipo: newTipo });
        },
      },
    );
  };

  const onTipoOutroDetalheChange = (detalhe: string) => {
    patchAndSave({ tipo_outro_detalhe: detalhe.trim() || null });
  };

  const onDepartamentoChange = (newDepartamento: SolicitacaoDepartamento | null) => {
    if (!task) return;
    const oldDepartamento = task.departamento_solicitacao ?? null;
    patchAndSave(
      // Mudou de equipe (#6518): sai do responsável antigo e volta para a fila sem dono da equipe nova.
      {
        departamento_solicitacao: newDepartamento, tipo_solicitacao: null,
        ...(equipeDoDepartamento(newDepartamento)?.id !== equipeDoDepartamento(oldDepartamento)?.id ? { responsavel_id: null } : {}),
        // Etapa que a equipe nova não tem (Em Análise/Em Teste da TI) vira "Em andamento", senão sumiria do quadro.
        ...(!etapasDoDepartamento(newDepartamento).includes(task.solicitacao_status || 'novo') ? { solicitacao_status: 'em_desenvolvimento' as SolicitacaoStatus } : {}),
      },
      {
        acaoLog: 'mudar_departamento_solicitacao',
        auditoria: async () => {
          await logAction(user?.id, 'UPDATE_TAREFA_DEPARTAMENTO_SOLICITACAO', { tarefa_id: task.id, old_departamento: oldDepartamento, new_departamento: newDepartamento });
          // Mudou de equipe (#6518): avisa os líderes da equipe nova, como na criação (TaskModal).
          const nova = equipeDoDepartamento(newDepartamento);
          if (nova && nova.id !== equipeDoDepartamento(oldDepartamento)?.id) {
            const lideres = nova.avisar.filter(id => id !== user?.id);
            if (lideres.length > 0) {
              await supabase.from('notifications').insert(lideres.map(id => ({
                user_id: id,
                type: 'task_assigned',
                title: 'Nova solicitação para a sua equipe',
                content: `Encaminhada para ${nova.label}: ${task.titulo}`,
                // Formato de solicitação: o sino abre o modal direto, sem navegar (idDaSolicitacaoNoLink).
                // O genérico "/tarefas?id=" navega pra uma rota que vendedor do Comercial Interno não acessa.
                link: `/tarefas/solicitacoes?id=${task.id}`,
              })));
            }
          }
        },
      },
    );
  };

  const onBloqueadaPorPessoaChange = (newPessoaId: string | null) => {
    if (!task) return;
    const oldPessoaId = task.bloqueada_por_pessoa_id ?? null;
    patchAndSave(
      { bloqueada_por_pessoa_id: newPessoaId },
      {
        acaoLog: 'bloquear_tarefa',
        auditoria: async () => {
          await logAction(user?.id, 'UPDATE_TAREFA_BLOQUEADA_POR_PESSOA', { tarefa_id: task.id, old_pessoa_id: oldPessoaId, new_pessoa_id: newPessoaId });
        },
      },
    );
  };

  const onBloqueadaPorTextoChange = (newTexto: string) => {
    patchAndSave({ bloqueada_por_texto: newTexto.trim() || null });
  };

  // #6358: tirar ou recolocar a dependência num gesto só (pessoa + motivo juntos). O
  // histórico é gravado pelo banco (trg_tarefas_historico_dependencia).
  const onDefinirDependencia = (pessoaId: string | null, texto: string | null) => {
    patchAndSave({ bloqueada_por_pessoa_id: pessoaId, bloqueada_por_texto: texto?.trim() || null }, { acaoLog: pessoaId || texto ? 'bloquear_tarefa' : 'resolver_dependencia' });
  };

  const onSolicitanteChange = (newSolicitanteId: string | null) => {
    if (!task) return;
    const oldSolicitanteId = task.solicitante_id ?? null;
    // `newSolicitanteId` vai cru (não `|| undefined`) porque este objeto vira o `.update(patch)`
    // do Supabase também — undefined faria o Supabase OMITIR o campo do update (não limpa a
    // coluna); precisa mandar `null` de verdade pra "voltar pra Sistema" funcionar.
    patchAndSave(
      { solicitante_id: newSolicitanteId },
      {
        acaoLog: 'mudar_solicitante',
        auditoria: async () => {
          await logAction(user?.id, 'UPDATE_TAREFA_SOLICITANTE', { tarefa_id: task.id, old_solicitante_id: oldSolicitanteId, new_solicitante_id: newSolicitanteId });
        },
      },
    );
  };

  const onProjetoChange = (projetoId: number | null) => {
    patchAndSave({ projeto_id: projetoId });
  };

  const onSaveTitle = async (titulo: string) => {
    if (!task) return;
    await api.tarefas.update(task.id, { titulo });
    logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'editar_titulo_tarefa', origem: 'acao' });
    setTask(prev => prev ? { ...prev, titulo } : prev);
    window.dispatchEvent(new CustomEvent(SOLICITACAO_ATUALIZADA_EVENTO, { detail: { id: task.id, patch: { titulo } } }));
  };

  const onSaveDescription = async (descricao: string) => {
    if (!task) return;
    await api.tarefas.update(task.id, { descricao });
    logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'editar_descricao_tarefa', origem: 'acao' });
    setTask(prev => prev ? { ...prev, descricao } : prev);
    window.dispatchEvent(new CustomEvent(SOLICITACAO_ATUALIZADA_EVENTO, { detail: { id: task.id, patch: { descricao } } }));
  };

  const onSelfAssign = () => {
    if (!user || !task) return;
    const patch: Partial<Tarefa> = { responsavel_id: user.id, data_inicio: new Date().toISOString().slice(0, 10) };
    (async () => {
      try {
        await api.tarefas.update(task.id, patch);
        logUsageEvent({ userId: user.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'assumir_tarefa', origem: 'acao' });
        setTask(prev => prev ? { ...prev, ...patch } : prev);
        window.dispatchEvent(new CustomEvent(SOLICITACAO_ATUALIZADA_EVENTO, { detail: { id: task.id, patch } }));
        addToast('Você assumiu esta solicitação.', 'success');
      } catch (error: any) {
        addToast('Erro ao atribuir: ' + (error?.message || JSON.stringify(error)), 'error');
      }
    })();
  };

  const onReagendar = async (date: string) => {
    if (!task) return;
    try {
      await api.tarefas.update(task.id, { vencimento: date });
      logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'reagendar_tarefa', origem: 'acao' });
      setTask(prev => prev ? { ...prev, vencimento: date } : prev);
      window.dispatchEvent(new CustomEvent(SOLICITACAO_ATUALIZADA_EVENTO, { detail: { id: task.id, patch: { vencimento: date } } }));
    } catch (error) {
      addToast('Erro ao reagendar', 'error');
    }
  };

  // Sobe e vincula num gesto só, pelo banco. A lista que volta é a do BANCO,
  // nunca `[...task.anexos_url, url]` — era essa montagem local que apagava o
  // anexo posto por outra pessoa enquanto esta tela estava aberta (4906).
  const onAddAttachment = async (file: File) => {
    if (!task) return;
    try {
      const listaAtualizada = await anexarArquivoNaTarefa(task.id, file);

      setTask(prev => prev ? { ...prev, anexos_url: listaAtualizada } : prev);
      window.dispatchEvent(new CustomEvent(SOLICITACAO_ATUALIZADA_EVENTO, { detail: { id: task.id, patch: { anexos_url: listaAtualizada } } }));
      await logAction(user?.id, 'ADD_ANEXO_TAREFA', { tarefa_id: task.id, arquivo: file.name });
      logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'anexar_arquivo_tarefa', origem: 'acao' });
    } catch (error: any) {
      addToast(error?.message || 'Erro ao enviar anexo.', 'error', 8000);
    }
  };

  // Simétrico ao acréscimo: quem tira da lista é o banco. Filtrar a lista da
  // tela e gravar por cima ressuscitava anexo já removido por outra pessoa.
  const onRemoveAttachment = (url: string) => {
    if (!task) return;
    (async () => {
      try {
        const listaAtualizada = await removerAnexoDaTarefa(task.id, url);
        logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'remover_anexo_tarefa', origem: 'acao' });
        setTask(prev => prev ? { ...prev, anexos_url: listaAtualizada } : prev);
        window.dispatchEvent(new CustomEvent(SOLICITACAO_ATUALIZADA_EVENTO, { detail: { id: task.id, patch: { anexos_url: listaAtualizada } } }));
      } catch (error: any) {
        addToast(error?.message || 'Erro ao remover anexo.', 'error', 8000);
      }
    })();
  };

  const onAddSubtask = async (data: { titulo: string; responsavel_id: string; prioridade: TarefaPrioridade; vencimento: string }) => {
    if (!task || !data.titulo.trim()) return;
    try {
      const hasResponsavel = !!data.responsavel_id;
      const taskData = {
        titulo: data.titulo.trim(),
        descricao: '',
        responsavel_id: hasResponsavel ? data.responsavel_id : null,
        prioridade: data.prioridade,
        vencimento: data.vencimento || null,
        solicitante_id: user?.id,
        status: 'a_iniciar' as TarefaStatus,
        categoria: task.categoria,
        tipo_solicitacao: task.tipo_solicitacao || null,
        // A filha é da mesma equipe da mãe (#6518): sem isso ela escapa do filtro de equipe e da regra de quem vê.
        departamento_solicitacao: task.departamento_solicitacao || null,
        solicitacao_status: 'novo' as SolicitacaoStatus,
        parent_id: task.id,
        data_inicio: hasResponsavel ? new Date().toISOString().slice(0, 10) : null,
        checklists: [],
        created_at: new Date().toISOString(),
      };
      const { data: created, error } = await supabase.from('tarefas').insert([taskData]).select().single();
      if (error) throw error;

      setSubtarefas(prev => [created as Tarefa, ...prev]);
      window.dispatchEvent(new CustomEvent(SOLICITACAO_CRIADA_EVENTO, { detail: { subtarefa: created } }));
      await logAction(user?.id, 'CREATE_TAREFA', { tarefa_id: created.id, titulo: created.titulo, parent_id: task.id });
      logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'criar_subtarefa', origem: 'acao' });
      addToast('Subtarefa criada.', 'success');
    } catch (error: any) {
      if (error?.code === 'P0001' || (error?.message || '').includes('urgentes em aberto')) {
        addToast('Limite de 3 solicitações urgentes em aberto atingido.', 'error');
      } else {
        addToast('Erro ao criar subtarefa: ' + (error?.message || JSON.stringify(error)), 'error');
      }
      throw error;
    }
  };

  const uploadComentarioAnexos = async (files: File[]): Promise<string[]> => {
    if (!task) return [];
    // Mesmo envio do anexo da solicitação: caminho com sufixo aleatório (dois
    // prints colados no mesmo comentário caíam no mesmo milissegundo e o segundo
    // voltava "already exists") e nova tentativa em falha transitória.
    const urls: string[] = [];
    for (const file of files) {
      const { url } = await enviarAnexo(file, `comentarios/${task.id}`);
      urls.push(url);
    }
    return urls;
  };

  const onSendComment = async (text: string, mentionedUserIds: string[], files: File[] = []) => {
    if (!task) return;
    let anexosUrl: string[] = [];
    if (files.length > 0) {
      try {
        anexosUrl = await uploadComentarioAnexos(files);
      } catch (error: any) {
        addToast('Erro ao enviar o arquivo do comentário: ' + (error?.message || JSON.stringify(error)), 'error');
        throw error;
      }
    }

    const { data, error } = await supabase.from('comentarios_tarefas').insert([{
      tarefa_id: task.id,
      user_id: user?.id,
      conteudo: text,
      anexos_url: anexosUrl,
    }]).select('*, user:profiles(*)').single();

    if (error) {
      addToast('Erro ao enviar comentário: ' + error.message, 'error');
      throw error;
    }

    await logAction(user?.id, 'CREATE_COMENTARIO_TAREFA', { tarefa_id: task.id, comentario_id: data.id });
    logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'comentar_tarefa', origem: 'acao' });
    setComments(prev => [...prev, data as any]);

    if (mentionedUserIds.length > 0) {
      const notificationsToInsert = mentionedUserIds.map(mentionedUserId => ({
        user_id: mentionedUserId,
        type: 'mention',
        title: 'Você foi marcado em um comentário',
        content: `${profile?.full_name} mencionou você na tarefa "${task.titulo}"`,
        // Link de solicitação, não o genérico "/tarefas?id=" que TarefasView.tsx usa pra
        // tarefa comum — esta função só existe pra solicitação, então o link já nasce no
        // formato que NotificationDropdown.tsx sabe abrir sem navegar (idDaSolicitacaoNoLink).
        link: `/tarefas/solicitacoes?id=${task.id}`,
        read: false,
      }));
      await supabase.from('notifications').insert(notificationsToInsert);
    }
  };

  const onEditComment = async (commentId: number, text: string) => {
    const { error } = await supabase.from('comentarios_tarefas').update({ conteudo: text }).eq('id', commentId);
    if (error) {
      addToast('Erro ao editar comentário.', 'error');
      throw error;
    }
    setComments(prev => prev.map(c => c.id === commentId ? { ...c, conteudo: text } : c));
  };

  // #6287: o banco só apaga se quem pede for o autor; devolve NULL nos outros casos.
  const onDeleteComment = async (commentId: number) => {
    const { data, error } = await supabase.rpc('apagar_comentario_tarefa', { p_comentario_id: commentId });
    if (error || !data) {
      addToast(error ? mensagemErroApagarComentario(error) : 'Só quem escreveu o comentário pode apagar.', 'error');
      throw error || new Error('comentario_nao_apagado');
    }
    await logAction(user?.id, 'DELETE_COMENTARIO_TAREFA', { tarefa_id: task?.id, comentario_id: commentId });
    setComments(prev => prev.map(c => c.id === commentId
      ? ocultarSeApagado({ ...c, apagado_em: data as string, apagado_por: user?.id ?? null })
      : c));
  };

  const onRequestDelete = () => setRequestingDelete(true);
  const cancelDelete = () => setRequestingDelete(false);
  // Devolve se apagou de verdade — quem chama (SolicitacaoModalGlobal.tsx) só fecha o modal
  // inteiro quando for `true`; em erro o modal continua aberto (o toast já avisa) e o botão
  // Excluir segue disponível pra tentar de novo, em vez de sumir o modal na cara do usuário.
  const confirmDelete = async (): Promise<boolean> => {
    if (!task) return false;
    setDeleting(true);
    try {
      await api.tarefas.delete(task.id);
      await logAction(user?.id, 'DELETE_TAREFA', { tarefa_id: task.id });
      logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'excluir_tarefa', origem: 'acao' });
      window.dispatchEvent(new CustomEvent(SOLICITACAO_EXCLUIDA_EVENTO, { detail: { id: task.id } }));
      setRequestingDelete(false);
      return true;
    } catch (error) {
      addToast('Erro ao excluir tarefa', 'error');
      return false;
    } finally {
      setDeleting(false);
    }
  };

  const onDirecionar = () => setDirecionando(true);
  const cancelDirecionar = () => setDirecionando(false);
  const confirmDirecionar = async (responsavelId: string, dueDate: string) => {
    if (!task) return;
    const patch: Partial<Tarefa> = {
      responsavel_id: responsavelId,
      data_inicio: new Date().toISOString().slice(0, 10),
      ...(dueDate ? { vencimento: dueDate } : {}),
    };
    try {
      await api.tarefas.update(task.id, patch);
      logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'transferir_tarefa', origem: 'acao' });
      setTask(prev => prev ? { ...prev, ...patch } : prev);
      window.dispatchEvent(new CustomEvent(SOLICITACAO_ATUALIZADA_EVENTO, { detail: { id: task.id, patch } }));

      const { data: prefs } = await supabase
        .from('notification_preferences')
        .select('task_assigned')
        .eq('user_id', responsavelId)
        .single();
      if (!prefs || prefs.task_assigned !== false) {
        await supabase.from('notifications').insert([{
          user_id: responsavelId,
          type: 'task_assigned',
          title: 'Solicitação direcionada',
          content: `${profile?.full_name || 'Alguém'} direcionou a solicitação "${task.titulo}" para você.`,
          link: `/tarefas/solicitacoes?id=${task.id}`,
          read: false,
        }]);
      }
      setDirecionando(false);
    } catch (error: any) {
      addToast('Erro ao direcionar: ' + (error?.message || JSON.stringify(error)), 'error');
    }
  };

  const onOpenSubtask = (subtask: Tarefa) => abrirSolicitacao(String(subtask.id));

  return {
    task, loading, notFound, subtarefas, comments,
    requestingDelete, deleting, direcionando,
    onStatusChange, onTipoChange, onTipoOutroDetalheChange, onDepartamentoChange,
    onBloqueadaPorPessoaChange, onBloqueadaPorTextoChange, onDefinirDependencia, onSolicitanteChange,
    onProjetoChange, onSaveTitle, onSaveDescription, onSelfAssign, onReagendar,
    onAddAttachment, onRemoveAttachment, onAddSubtask, onSendComment, onEditComment, onDeleteComment,
    onRequestDelete, cancelDelete, confirmDelete,
    onDirecionar, cancelDirecionar, confirmDirecionar,
    onOpenSubtask,
  };
};
